import React, { useEffect, useState } from 'react';
import { styled, useMediaQuery } from '@mui/material';
import theme from 'theme';
import { Colors } from 'library';

import { RLayoutTopBar } from './RLayoutTopBar';
import { RLayoutSidebar, SIDEBAR_WIDTH, RNavItem, AppMode } from './RLayoutSidebar';
import { RBanner, RBannerConfig } from './RBanner';

interface ModeToggleConfig {
    mode: AppMode;
    onModeChange: (mode: AppMode) => void;
}

interface RLayoutProps {
    children: React.ReactNode;
    navItems: RNavItem[];
    bottomNavItems?: RNavItem[];
    settingsNavItems?: RNavItem[];
    modeToggle?: ModeToggleConfig;
    extraBottomContent?: React.ReactNode;
    bannerConfig?: RBannerConfig;
    showBottomSection?: boolean;
    initials?: string;
    avatarImageUrl?: string | null;
    onAvatarClick?: () => void;
}

export function RLayout({
    children,
    navItems,
    bottomNavItems,
    settingsNavItems,
    modeToggle,
    extraBottomContent,
    bannerConfig,
    showBottomSection = true,
    initials,
    avatarImageUrl,
    onAvatarClick,
}: RLayoutProps) {
    const small = useMediaQuery(theme.breakpoints.down('md'));
    const [sidebarOpen, setSidebarOpen] = useState(!small);

    useEffect(() => {
        setSidebarOpen(!small);
    }, [small]);

    return (
        <Root>
            <RLayoutTopBar
                small={small}
                onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                initials={initials ?? 'MM'}
                avatarImageUrl={avatarImageUrl}
                onAvatarClick={onAvatarClick}
            />
            <RLayoutSidebar
                small={small}
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                navItems={navItems}
                bottomNavItems={bottomNavItems}
                settingsNavItems={settingsNavItems}
                modeToggle={modeToggle}
                extraBottomContent={extraBottomContent}
                showBottomSection={showBottomSection}
            />
            <Main sidebarOpen={sidebarOpen} small={small}>
                {bannerConfig && (
                    <RBanner variant={bannerConfig.variant} badge={bannerConfig.badge} action={bannerConfig.action}>
                        {bannerConfig.message}
                    </RBanner>
                )}
                {children}
            </Main>
        </Root>
    );
}

const Root = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: Colors.white,
});

const Main = styled('main')<{ sidebarOpen: boolean; small: boolean }>(({ sidebarOpen, small }) => ({
    flexGrow: 1,
    marginLeft: sidebarOpen && !small ? SIDEBAR_WIDTH : 0,
    marginTop: small ? 50 : 60,
    transition: 'margin-left 0.2s ease',
    padding: 24,
    backgroundColor: Colors.faintGrey,
    minHeight: 'calc(100vh - 60px)',
}));
